import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useParams,
} from 'react-router-dom';

import { HomeRedirect } from '~/app/HomeRedirect';
import { LandingLayout } from '~/app/landing/LandingLayout';
import { LoginScreen } from '~/app/login/LoginScreen';

import { AdminLandingPageContainer } from '~/app/landing/admin/AdminLandingPageContainer';
import { BackOfficePageContainer } from '~/app/landing/admin/back-office/BackOfficePageContainer';
import { OrganizationRelationshipPageContainer } from '~/app/landing/admin/back-office/organization-relationship/OrganizationRelationshipPageContainer';
import { OrganizationPageContainer } from '~/app/landing/admin/back-office/organizations/detail/OrganizationPageContainer';
import { PlatformSettingsPage } from '~/app/landing/admin/back-office/settings/PlatformSettingsPage/PlatformSettingsPage';
import { PipelineDebugPageContainer } from '~/app/landing/admin/pipeline-debug/PipelineDebugPageContainer';
import { LogsExportPageContainer } from '~/app/landing/admin/logs-export/LogsExportPageContainer';
import { ComponentSchemaScreen } from '~/app/landing/admin/component-schema/ComponentSchemaScreen';
import { ErrorManagementPageContainer } from '~/app/landing/admin/error-management/ErrorManagementPageContainer';
import { DiagnosticRulesPageContainer } from '~/app/landing/admin/diagnostic-rules/DiagnosticRulesPageContainer';
import { DashboardPageContainer } from '~/app/landing/dashboard/DashboardPageContainer';
import { KmsPageContainer } from '~/app/landing/kms/KmsPageContainer';
import { KmsDocumentDetailPageContainer } from '~/app/landing/kms/document-detail/KmsDocumentDetailPageContainer';
import { HomeScreen } from '~/app/landing/home/HomeScreen';
import { IncidentDetailPageContainer } from '~/app/landing/home/incidents/detail/IncidentDetailPageContainer';
import { ManualUploadScreen } from '~/app/landing/manual-upload/ManualUploadScreen';
import { OperatorSectionPageContainer } from '~/app/landing/operator/OperatorSectionPageContainer';
import { PortfolioPageContainer } from '~/app/landing/operator/portfolio/PortfolioPageContainer';
import { ScorecardPageContainer } from '~/app/landing/operator/scorecard/ScorecardPageContainer';
import { ServiceQualityPageContainer } from '~/app/landing/operator/service-quality/ServiceQualityPageContainer';
import { AuthGuard } from '~/app/auth/guard/AuthGuard';
import { PermissionKey } from '@evimo/types/enums/permission-key';
import { SessionHome } from '~/app/landing/session/SessionHome';
import { SessionChargePage } from '~/app/landing/session/SessionChargePage';
import { IncidentOverviewContainer } from '~/app/components/incident-overview/IncidentOverviewContainer';
import { RolesPageContainer } from '~/app/landing/admin/roles/RolesPageContainer';
import { RoleDetailPageContainer } from '~/app/landing/admin/roles/RoleDetailPageContainer';
import { PeoplePageContainer } from '~/app/landing/people/PeoplePageContainer';
import { LandlordsPageContainer } from '~/app/landing/sla/landlords/LandlordsPageContainer';
import { OemsPageContainer } from '~/app/landing/sla/oems/OemsPageContainer';

import { AssetOverviewContainer } from '~/app/components/asset-overview';
import { AssetDetailPage } from '~/app/components/asset-detail/AssetDetailPage';
import { AssetCustomFieldsPageContainer } from '~/app/landing/assets/custom-fields/AssetCustomFieldsPageContainer';
import { SiteManagementContainer } from '~/app/components/site-management';
import { SiteAssetMapPageContainer } from '~/app/components/site-asset-map';
import { ZoneManagementContainer } from '~/app/components/zone-management';
import { ZoneAssetMapPageContainer } from '~/app/components/zone-asset-map';

import { TasksTriggersPageContainer } from '~/app/components/maintenance/tasks-triggers/TasksTriggersPageContainer/TasksTriggersPageContainer';
import { PreventiveMaintenancePageContainer } from '~/app/components/maintenance/preventive/PreventiveMaintenancePageContainer/PreventiveMaintenancePageContainer';
import { PlanDetailPageContainer } from '~/app/components/maintenance/preventive/PlanDetailPageContainer/PlanDetailPageContainer';
import { PlanWizardPage } from '~/app/components/maintenance/preventive/wizard/PlanWizardPage/PlanWizardPage';
import { WorkOrderKanbanPageContainer } from '~/app/components/maintenance/work-orders/WorkOrderKanbanPageContainer/WorkOrderKanbanPageContainer';
import { WorkOrderOptimizerPageContainer } from '~/app/components/maintenance/work-orders/WorkOrderOptimizerPageContainer/WorkOrderOptimizerPageContainer';
import { TicketDetailPageContainer } from '~/app/components/maintenance/work-orders/TicketDetailPageContainer/TicketDetailPageContainer';

import { AssetImportMatchyScreen } from '~/app/assets/import/components/assetImportMatchy';
import { AssetImportMappingScreen } from '~/app/assets/import/AssetImportMappingScreen';
import { ProfileContainer } from '~/app/components/Profile/index';

function AssetImportMappingRedirect() {
  const { importRequestId = '' } = useParams<{ importRequestId: string }>();
  return <Navigate to={`/assets/import/${importRequestId}/mapping`} replace />;
}

function LegacyHomeIncidentDetailRedirect() {
  const { incidentId = '' } = useParams<{ incidentId: string }>();
  return <Navigate to={`/incidents/${incidentId}`} replace />;
}

export const router = createBrowserRouter([
  { path: '/', element: <HomeRedirect /> },
  { path: '/login', element: <LoginScreen /> },
  {
    element: <LandingLayout />,
    children: [
      { path: 'account/profile', element: <ProfileContainer /> },
      { path: 'people', element: <PeoplePageContainer /> },
      { path: 'dashboard', element: <DashboardPageContainer /> },
      { path: 'home', element: <HomeScreen /> },
      {
        path: 'operator/portfolio',
        element: <PortfolioPageContainer />,
      },
      {
        path: 'operator/scorecard',
        element: <ScorecardPageContainer />,
      },
      {
        path: 'operator/service-quality',
        element: <ServiceQualityPageContainer />,
      },
      {
        path: 'operator/compliance',
        element: <OperatorSectionPageContainer />,
      },
      {
        path: 'operator/reports',
        element: <OperatorSectionPageContainer />,
      },
      { path: 'incidents', element: <IncidentOverviewContainer /> },
      {
        path: 'incidents/:incidentId',
        element: <IncidentDetailPageContainer />,
      },
      {
        path: 'home/incidents',
        element: <Navigate to="/incidents" replace />,
      },
      {
        path: 'home/incidents/:incidentId',
        element: <LegacyHomeIncidentDetailRedirect />,
      },
      { path: 'sla/landlords', element: <LandlordsPageContainer /> },
      { path: 'sla/oems', element: <OemsPageContainer /> },
      {
        path: 'documents',
        element: <Navigate to="/kms" replace />,
      },
      { path: 'kms', element: <KmsPageContainer /> },
      {
        path: 'kms/documents/:documentId',
        element: <KmsDocumentDetailPageContainer />,
      },
      {
        path: 'reports',
        element: <Navigate to="/kms" replace />,
      },
      { path: 'manual-upload', element: <ManualUploadScreen /> },
      {
        path: 'sessions',
        element: (
          <AuthGuard requiredPermissions={[PermissionKey.SESSION_READ]}>
            <Outlet />
          </AuthGuard>
        ),
        children: [
          { index: true, element: <SessionHome /> },
          { path: ':chargeId', element: <SessionChargePage /> },
        ],
      },
      {
        path: 'session/*',
        element: <Navigate to="/sessions" replace />,
      },
      {
        path: 'supervisor',
        element: <Navigate to="/home" replace />,
      },
      {
        path: 'technician',
        element: <Navigate to="/home" replace />,
      },
      { path: 'admin', element: <AdminLandingPageContainer /> },
      { path: 'admin/component-schema', element: <ComponentSchemaScreen /> },
      {
        path: 'admin/error-management',
        element: <ErrorManagementPageContainer />,
      },
      {
        path: 'admin/diagnostic-rules',
        element: <DiagnosticRulesPageContainer />,
      },
      { path: 'admin/roles', element: <RolesPageContainer /> },
      {
        path: 'admin/roles/create',
        element: <RolesPageContainer openCreateOnMount />,
      },
      { path: 'admin/roles/:roleId', element: <RoleDetailPageContainer /> },
      { path: 'admin/back-office', element: <BackOfficePageContainer /> },
      {
        path: 'admin/back-office/settings',
        element: <PlatformSettingsPage />,
      },
      { path: 'admin/pipeline-debug', element: <PipelineDebugPageContainer /> },
      { path: 'admin/logs-export', element: <LogsExportPageContainer /> },
      {
        path: 'admin/back-office/organization-relationship',
        element: <OrganizationRelationshipPageContainer />,
      },
      {
        path: 'admin/back-office/organizations/:orgId/*',
        element: <OrganizationPageContainer />,
      },
      // Static asset routes before :assetId to avoid treating "import" / "custom-fields" as ids
      {
        path: 'assets/import',
        element: <AssetImportMatchyScreen />,
      },
      {
        path: 'assets/import/matchy',
        element: <AssetImportMatchyScreen />,
      },
      {
        path: 'assets/import/:importRequestId/mapping',
        element: <AssetImportMappingScreen />,
      },
      {
        path: 'assets/custom-fields',
        element: <AssetCustomFieldsPageContainer />,
      },
      { path: 'assets', element: <AssetOverviewContainer /> },
      { path: 'assets/:assetId', element: <AssetDetailPage /> },
      { path: 'sites', element: <SiteManagementContainer /> },
      { path: 'sites/:siteId', element: <SiteAssetMapPageContainer /> },
      {
        path: 'sites/:siteId/zones/:zoneId',
        element: <ZoneAssetMapPageContainer />,
      },
      { path: 'zones', element: <ZoneManagementContainer /> },
      {
        path: 'maintenance/preventive',
        element: <PreventiveMaintenancePageContainer />,
      },
      { path: 'maintenance/preventive/create', element: <PlanWizardPage /> },
      {
        path: 'maintenance/preventive/:planId',
        element: <PlanDetailPageContainer />,
      },
      {
        path: 'maintenance/tasks-triggers',
        element: <TasksTriggersPageContainer />,
      },
      {
        path: 'maintenance/work-orders/kanban',
        element: <WorkOrderKanbanPageContainer />,
      },
      {
        path: 'maintenance/work-orders/optimizer',
        element: <WorkOrderOptimizerPageContainer />,
      },
      {
        path: 'maintenance/work-orders/tickets/:ticketId',
        element: <TicketDetailPageContainer />,
      },
      {
        path: 'asset-import',
        element: <Navigate to="/assets/import" replace />,
      },
      {
        path: 'asset-import/:importRequestId/mapping',
        element: <AssetImportMappingRedirect />,
      },
    ],
  },
]);
